import { Box, Grid, Paper, useTheme } from "@mui/material";
import { FC, useMemo } from "react";
import { Link, Outlet } from "react-router-dom";
import { AgentPersonnelSummary, Permissions } from "../../api/dtos";
import GridActions from "../../components/GridActions";
import SetLinks from "../../components/layout/SetLinks";
import { StyledButton } from "../../components/StyledButton";
import CollapsibleAppTable from "../../components/tables/CollapsibleAppTable";
import type { AppColumn } from "../../components/tables/AppTable.types";
import AgentPersonnelSummariesContext from "../../contexts/AgentPersonnelContext";
import { DefaultProjectCreateLink } from "../../contexts/layouts/LinkContext";
import useAgentPersonnelSummaries from "../../hooks/data/useAgentPersonnelSummaries";

function requiredAgentPersonnelId(row: AgentPersonnelSummary): number {
    if (row.id === undefined || row.id === null) {
        throw new Error("Agent personnel summary is missing its ID.");
    }

    return row.id;
}

const useAgentLocationColumns = () => {
    const agentColumns = useMemo(() => {
        const columns: AppColumn<AgentPersonnelSummary>[] = [
            {
                id: "agentName",
                label: "Agent",
                scalarType: "text",
                accessor: (row) => row.agentName,
                flex: 1,
            },
            {
                id: "location",
                label: "Location",
                scalarType: "text",
                accessor: (row) => row.location,
                flex: 1,
            },
            {
                id: "personnel",
                label: "Personnel",
                scalarType: "text",
                accessor: (row) => row.personnel,
                flex: 1,
            },
            {
                id: "actions",
                label: "",
                headerLabel: "",
                kind: "action",
                scalarType: "text",
                accessor: (row) => String(row.id ?? ""),
                width: 100,
                cell: ({ row }) => (
                    <GridActions
                        editPermission={Permissions.AdminAccess}
                        editUrl={`/admin/agent-personnel/edit/${requiredAgentPersonnelId(row)}`}
                    />
                ),
            },
        ];

        return columns;
    }, []);

    return agentColumns;
};

const Agents: FC = () => {
    const columns = useAgentLocationColumns();

    const theme = useTheme();

    const context = useAgentPersonnelSummaries();

    const { data: agentPersonnel, loading: agentPersonnelLoading } = context;

    const exportMetadata = useMemo(
        () => ({
            fileName: `Agent Personnel`,
            exportTitle: `Agent Personnel`,
            includeDateTime: true,
        }),
        [],
    );

    return (
        <AgentPersonnelSummariesContext.Provider value={context}>
            <SetLinks createProjectLink={DefaultProjectCreateLink} stageLinkEnabled={false} lotLinkEnabled={false} />
            <Outlet />
            <Grid container gap={theme.spacing(2)} justifyContent={"end"} marginBottom={theme.spacing(2)}>
                <StyledButton
                    variant="contained"
                    //@ts-expect-error
                    component={Link}
                    to={`/admin/agent-personnel/create`}
                    state={{
                        back: true,
                    }}
                >
                    Add Agent Personnel
                </StyledButton>
            </Grid>
            <Paper>
                <Box>
                    <CollapsibleAppTable
                        density="compact"
                        startCollapsed={false}
                        title="Agent Personnel"
                        pluralizeTitle={false}
                        pluralizeEntity={false}
                        loading={agentPersonnelLoading}
                        rows={agentPersonnel ?? []}
                        columns={columns}
                        getRowId={requiredAgentPersonnelId}
                        columnControls={{
                            menu: true,
                        }}
                        filterControls
                        export={exportMetadata}
                    />
                </Box>
            </Paper>
        </AgentPersonnelSummariesContext.Provider>
    );
};

export default Agents;
